import { PropsWithChildren, useEffect, useMemo } from 'react';
import { FallbackProps } from 'react-error-boundary';
import { isAxiosError } from 'axios';
import { GlobalError } from '@antimatterhq/ui/components/globalError';
import { useVerifyAuth } from '@/hooks/dbui/useVerifyAuth';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { SuspenseErrorBoundary } from '@/components/errorBoundary/SuspenseErrorBoundary';
import { PageHeader } from '@/components/pageHeader/PageHeader';
import { useConfig } from '@/contexts/ConfigContext';
import { useClientAuthentication } from '@/hooks/authentication/useClientAuthentication';
import { useInstanceId } from '@/contexts/InstanceIdContext';

const VerifyAuth: React.FC<PropsWithChildren> = ({ children }) => {
  const { refreshAuthentication } = useClientAuthentication();
  const { workspace, setWorkspace } = useWorkspace();
  const { data } = useVerifyAuth();
  const { instanceId } = useInstanceId();
  const { MSW_ENABLE_MOCKING, NODE_ENV } = useConfig();

  // At this point, we can guarantee that the user is authenticated (non 401) but for some reason not authed with databricks
  useEffect(() => {
    const refresh = async () => {
      if (
        !data.isDatabricksAuthed ||
        (instanceId && instanceId !== data.daslWorkspaceId)
      ) {
        await refreshAuthentication();
      } else {
        // If we didnt need to auth, then the user is already authenticated and we can set the workspace
        if (MSW_ENABLE_MOCKING && NODE_ENV === 'development') {
          setWorkspace('default');
        } else {
          setWorkspace(data.daslWorkspaceId);
        }
      }
    };

    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return !data.isDatabricksAuthed ||
    (instanceId && instanceId !== data.daslWorkspaceId) ||
    !workspace ? null : (
    <>{children}</>
  );
};

const AuthErrorBoundaryFallback: React.FC<FallbackProps> = (props) => {
  const isAuthenticationError = useMemo(
    () => isAxiosError(props.error) && props.error.response?.status === 401,
    [props.error],
  );

  return (
    <main className="flex h-screen w-screen flex-col items-center justify-center gap-4 p-4">
      <PageHeader pageTitle="" />
      <div className="flex h-full flex-col items-center justify-center gap-4">
        {!isAuthenticationError ? (
          <GlobalError
            title="Something went wrong"
            description={props.error.message}
            onRetry={async () => props.resetErrorBoundary()}
            retryText="Retry"
          />
        ) : null}
      </div>
    </main>
  );
};

export default function AuthHandler({ children }: PropsWithChildren) {
  return (
    <SuspenseErrorBoundary errorBoundaryFallback={AuthErrorBoundaryFallback}>
      <VerifyAuth>{children}</VerifyAuth>
    </SuspenseErrorBoundary>
  );
}
